import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { delay, useSetState, validArray } from 'utils';
// Assets
import Icon from 'assets/Icon';
// Elements
import Alert from 'elements/Alert';
import Button from 'elements/Button';
import SearchField from 'elements/Forms/components/SearchField';
import { ListItem } from 'elements/ListItems';
// Style
import styles from './style/listFinder.module.css';

interface ListFinderProps {
  disabled?: boolean,
  handleClear: Function,
  handleSelect: Function,
  items: string[],
  label: string,
  requireValue?: boolean,
  value: string,
}

const ListFinder = ({
  disabled,
  handleClear,
  handleSelect,
  items,
  label,
  requireValue,
  value,
}:ListFinderProps) => {
  // Ref
  const select = useRef<HTMLDivElement>(null);
  // State
  const initialState = useMemo(() => ({
    filteredItems: requireValue ? [] : items.slice(0, 200),
    filterValue: '',
    loading: false,
    popOver: false,
  }), [items, requireValue]);
  const [state, setState] = useSetState(initialState);

  function setIcon() {
    if (state.popOver) {
      return 'Close';
    }
    return value ? 'Edit' : 'Search';
  }

  const filterItems = useCallback((item:string) => {
    if (requireValue) {
      return state.filterValue && item.includes(state.filterValue.toUpperCase());
    }
    return item.includes(state.filterValue.toUpperCase());
  }, [requireValue, state.filterValue]);

  function togglePopOver() {
    if (state.popOver) {
      setState(initialState);
    } else {
      setState({ popOver: true })
    }
  }

  const handleFilterChange = useCallback(async () => {
    await setState({
      loading: true,
    });
    await delay(500);
    setState({
      filteredItems: items.filter(filterItems).slice(0, 200),
      loading: false,
    })
  }, [filterItems, items, setState]);

  useEffect(() => {
    setState(initialState);
  }, [handleSelect, initialState, setState]);

  useEffect(() => {
    handleFilterChange();
  }, [handleFilterChange, state.filterValue]);

  function renderItem(item: string) {
    return (
      <ListItem handler={() => handleSelect(item)} key={item}>
        <span>{ item }</span>
        <Icon fill="warm-grey-200" icon="ArrowRight" />
      </ListItem>
    );
  }

  function getList() {
    if (state.loading) {
      return (
        <div className={styles.popOver__list__loading}>
          {[...Array(5)].map((e, i) => <div key={i.toString()} />)}
        </div> 
      )
    }
    return validArray(state.filteredItems)
      ? state.filteredItems.map(renderItem)
      : <div className={styles.noItems}><Alert active type="info" label="No Items Found" /></div>;
  }

  return (
    <div className={styles.container} ref={select}>
      <div className={`
        ${styles.listValue}
        ${value ? styles.listValue_hasValue : ''}
        ${disabled ? styles.listValue_disabled : ''}
      `}
      >
        <div>
          <div className={styles.listValue__label}>
            { value ? label : `Find ${label}` }
          </div>
          { value && (
            <div className={styles.listValue__value}>{ value }</div>
          )}
        </div>
        <div className={styles.listValue__actions}>
          { value && (
            <Button
              btnSize="small"
              btnType="icon"
              disabled={disabled}
              handler={() => handleClear()}
            >
              <Icon
                fill="warm-grey-200"
                icon="Bin"
                size={24}
              />
            </Button>
          )}
          <Button
            btnSize="small"
            btnType="icon"
            disabled={disabled}
            handler={() => togglePopOver()}
          >
            <Icon
              fill={state.popOver ? 'strawberry' : 'blueberry'}
              icon={setIcon()}
              size={24}
            />
          </Button>
        </div>
      </div>
      { state.popOver && (
        <div className={styles.popOver}>
          <div className={styles.popOver__list}>
            { getList() }
          </div>
          <footer>
            <SearchField
              autoFocus
              icon
              keydownHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ filterValue: e.target.value })}
              onChangeHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ filterValue: e.target.value })}
              onPasteHandler={(e:React.ChangeEvent<HTMLInputElement>) => setState({ filterValue: e.target.value })}
              placeholderText="Search"
              value={state.filterValue}
            />
          </footer>
        </div>
      )}
    </div>
  );
};

ListFinder.defaultProps = {
  disabled: false,
  requireValue: false,
}

export default ListFinder;
